<template>
  <div class="map-info-window">
    <!-- Custom Close Button -->
    <button 
      class="close-button" 
      @click="closeInfoWindow"
      aria-label="Close"
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M13 1L1 13M1 1L13 13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      </svg>
    </button>

    <!-- Single Event Display -->
    <div v-if="shows.length === 1" class="single-event">
      <!-- Cover Photo Header -->
      <div class="cover-photo-header" :style="coverPhotoStyle(shows[0])">
        <div class="photo-overlay">
          <h3 class="show-name">{{ shows[0].name }}</h3>
          <p class="show-date">{{ formatDateRange(shows[0].start_date, shows[0].end_date) }}</p>
        </div>
      </div>

      <!-- Show Details -->
      <div class="show-details">
        <!-- Location -->
        <div class="location-info">
          <svg class="location-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" stroke="currentColor" stroke-width="2"/>
            <circle cx="12" cy="10" r="3" stroke="currentColor" stroke-width="2"/>
          </svg>
          <div class="location-text">
            <p class="venue">{{ shows[0].location_details?.location_name || 'Venue TBD' }}</p>
            <p class="city-state">
              {{ shows[0].location_details?.city || 'City' }}, {{ shows[0].location_details?.state || 'State' }}
            </p>
          </div>
        </div>

        <!-- Species Tags -->
        <div v-if="shows[0].species && shows[0].species.length > 0" class="species-tags">
          <span 
            v-for="species in shows[0].species.slice(0, 3)" 
            :key="species.species" 
            class="species-tag"
          >
            {{ species.species }}
          </span>
          <span v-if="shows[0].species.length > 3" class="species-more">
            +{{ shows[0].species.length - 3 }} more
          </span>
        </div>

        <!-- Action Button -->
        <div class="action-section">
          <button class="view-details-btn" @click="viewSingleEventDetails(shows[0])">
            View Details
          </button>
        </div>
      </div>
    </div>

    <!-- Multiple Events Display -->
    <div v-else class="multiple-events">
      <!-- Header -->
      <div class="multiple-header">
        <div class="photo-overlay">
          <h3 class="events-count">{{ shows.length }} Events</h3>
          <p class="location-name">{{ getVenueName() }}</p>
        </div>
      </div>

      <!-- Events List -->
      <div class="events-list">
        <div 
          v-for="(show, index) in shows" 
          :key="show.id || index"
          class="event-item"
          @click="viewSingleEventDetails(show)"
        >
          <div class="event-info">
            <h4 class="event-name">{{ show.name }}</h4>
            <p class="event-date">{{ formatDateRange(show.start_date, show.end_date) }}</p>
          </div>
          <div class="event-chevron">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { parse } from 'date-fns'

export default {
  name: 'MapInfoWindow',
  props: {
    shows: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  computed: {
    // No longer needed as computed property since we pass show as parameter
  },
  mounted() {
    // Component mounted successfully
  },
  methods: {
    coverPhotoStyle(show) {
      if (show.event_details && show.event_details.event_card_cover_url) {
        return {
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.5)), url(${show.event_details.event_card_cover_url})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }
      } else {
        return {
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        }
      }
    },
    getLocationName() {
      if (this.shows.length === 0) return 'Location'
      
      const firstShow = this.shows[0]
      if (firstShow.location_details) {
        const city = firstShow.location_details.city
        const state = firstShow.location_details.state
        if (city && state) {
          return `${city}, ${state}`
        } else if (city) {
          return city
        } else if (state) {
          return state
        }
      }
      return 'Multiple Locations'
    },
    getVenueName() {
      if (this.shows.length === 0) return 'Location'
      
      const firstShow = this.shows[0]
      if (firstShow.location_details) {
        // If there's a specific venue name, show it
        if (firstShow.location_details.location_name) {
          return firstShow.location_details.location_name
        }
        // Otherwise fall back to city, state
        const city = firstShow.location_details.city
        const state = firstShow.location_details.state
        if (city && state) {
          return `${city}, ${state}`
        } else if (city) {
          return city
        } else if (state) {
          return state
        }
      }
      return 'Multiple Venues'
    },
    formatDateRange(startDate, endDate) {
      if (!startDate) return 'Date TBD'
      
      // date-fns parse reads date-only strings ("2027-05-07") as local midnight.
      // new Date() would read them as UTC midnight, which renders a day early
      // for every viewer west of UTC.
      const start = parse(startDate)
      const end = endDate ? parse(endDate) : null
      
      const options = { 
        month: 'short', 
        day: 'numeric',
        year: start.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
      }
      
      if (end && start.toDateString() !== end.toDateString()) {
        return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`
      } else {
        return start.toLocaleDateString('en-US', options)
      }
    },
    viewSingleEventDetails(show) {
      // Navigate to specific show details page
      this.$router.push(`/${show.event_identifier}`)
    },
    viewAllEvents() {
      // Emit event to parent with all shows for handling
      this.$emit('view-all-events', this.shows)
    },
    closeInfoWindow() {
      // Emit event to parent component to close the info window
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.map-info-window {
  position: relative;
  min-width: 280px;
  max-width: 320px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  margin: 0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  background: white;
}

.close-button {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 10;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  backdrop-filter: blur(4px);
}

.close-button:hover {
  background: rgba(0, 0, 0, 0.8);
  transform: scale(1.05);
}

.close-button:active {
  transform: scale(0.95);
}

/* Single Event Styles */
.cover-photo-header {
  height: 120px;
  background-color: #f3f4f6;
  border-radius: 12px 12px 0 0;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: flex-end;
}

.photo-overlay {
  padding: 16px;
  width: 100%;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
}

.show-name {
  font-size: 18px;
  font-weight: 700;
  color: white;
  margin: 0 0 4px 0;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
  line-height: 1.2;
}

.show-date {
  font-size: 14px;
  color: white;
  margin: 0;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  opacity: 0.95;
}

.show-details {
  padding: 16px;
}

.location-info {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 12px;
}

.location-icon {
  color: #6b7280;
  margin-top: 2px;
  flex-shrink: 0;
}

.location-text {
  flex: 1;
}

.venue {
  font-weight: 600;
  color: #111827;
  margin: 0 0 2px 0;
  font-size: 14px;
}

.city-state {
  color: #6b7280;
  margin: 0;
  font-size: 13px;
}

.species-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 16px;
}

.species-tag {
  background: #e5e7eb;
  color: #374151;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.species-more {
  color: #6b7280;
  font-size: 12px;
  padding: 4px 0;
}

/* Multiple Events Styles */
.multiple-header {
  height: 80px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px 12px 0 0;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: flex-end;
}

.events-count {
  font-size: 20px;
  font-weight: 700;
  color: white;
  margin: 0 0 4px 0;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
  line-height: 1.2;
}

.location-name {
  font-size: 14px;
  color: white;
  margin: 0;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  opacity: 0.95;
}

.events-list {
  max-height: 200px;
  overflow-y: auto;
}

.event-item {
  padding: 12px 16px;
  border-bottom: 1px solid #f3f4f6;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.event-item:hover {
  background-color: #f9fafb;
}

.event-item:last-child {
  border-bottom: none;
}

.event-info {
  flex: 1;
}

.event-name {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 4px 0;
  line-height: 1.3;
}

.event-date {
  font-size: 12px;
  color: #6b7280;
  margin: 0 0 2px 0;
}

.event-venue {
  font-size: 12px;
  color: #9ca3af;
  margin: 0;
  font-style: italic;
}

.event-chevron {
  color: #d1d5db;
  margin-left: 8px;
}

.more-events {
  padding: 8px 16px;
  background-color: #f9fafb;
  border-top: 1px solid #f3f4f6;
}

.more-text {
  font-size: 12px;
  color: #6b7280;
  margin: 0;
  text-align: center;
  font-style: italic;
}

/* Action Buttons */
.action-section {
  border-top: 1px solid #f3f4f6;
  padding: 12px 16px;
  margin: 0;
}

.view-details-btn,
.view-all-btn {
  width: 100%;
  background: #3b82f6;
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.view-details-btn:hover,
.view-all-btn:hover {
  background: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.view-details-btn:active,
.view-all-btn:active {
  transform: translateY(0);
}

/* Override Google Maps default styles */
:global(.gm-style-iw) {
  padding: 0 !important;
  border-radius: 12px !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12) !important;
}

:global(.gm-style-iw-d) {
  overflow: hidden !important;
  padding: 0 !important;
  margin: 0 !important;
}

:global(.gm-style-iw-c) {
  padding: 0 !important;
  margin: 0 !important;
  border-radius: 12px !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12) !important;
}

:global(.gm-style-iw-ch) {
  padding: 0 !important;
  padding-top: 0 !important;
  margin: 0 !important;
  margin-top: 0 !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  position: relative !important;
}

/* More specific selectors to override Google's styles */
:global(.gm-style-iw .gm-style-iw-d) {
  padding: 0 !important;
  margin: 0 !important;
}

:global(.gm-style-iw .gm-style-iw-chr) {
  padding: 0 !important;
  padding-top: 0 !important;
  margin: 0 !important;
  margin-top: 0 !important;
}

:global(.gm-style-iw-d .gm-style-iw-chr) {
  padding: 0 !important;
  padding-top: 0 !important;
  margin: 0 !important;
  margin-top: 0 !important;
}

/* Hide the default close button - simplified approach */
:global(.gm-style-iw-tc) {
  display: none !important;
}

:global(.gm-ui-hover-effect) {
  display: none !important;
}
</style> 